/**
 * AuthManagerContext
 *
 * React context for providing AuthManager instance throughout the SDK.
 * Handles initialization, callback processing, and auth state changes.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import { AuthManager, AuthConfig, UserProfile } from '../lib/auth-manager';
import { ApiClient } from '../lib/api-client';

interface AuthManagerContextValue {
  authManager: AuthManager | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  currentUser: UserProfile | null;
  internalUserId: string | null;
  idToken: string | null;  // For API requests (hackweek - TODO: upgrade to MAS token)
}

const AuthManagerContext = createContext<AuthManagerContextValue | null>(null);

export interface AuthManagerProviderProps {
  children: ReactNode;
  config: {
    siteId: string;
    environmentId?: string;
    apiUrl: string;
  };
}

/**
 * AuthManagerProvider - Provides AuthManager instance to the SDK
 *
 * Handles:
 * - AuthManager initialization with config from environment
 * - Processing auth callback on mount (URL fragment with token)
 * - Tracking authentication state changes
 */
export const AuthManagerProvider: React.FC<AuthManagerProviderProps> = ({
  children,
  config,
}) => {
  // Create API client for token exchange
  const apiClient = useMemo(() => new ApiClient({
    baseUrl: config.apiUrl,
    siteId: config.siteId,
    environmentId: config.environmentId || 'default',
  }), [config.apiUrl, config.siteId, config.environmentId]);

  // Create AuthManager instance first (so we can read initial state from it)
  const authManager = useMemo(() => {
    // Get auth config from environment variables
    const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || import.meta.env.VITE_APP_AUTH0_DOMAIN || '';
    const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || import.meta.env.VITE_APP_AUTH0_CLIENT_ID || '';
    const callbackUrl = import.meta.env.VITE_AUTH_CALLBACK_URL || '';

    // If config is missing, we can't create AuthManager
    if (!auth0Domain || !auth0ClientId || !callbackUrl) {
      console.warn(
        '[AuthManager] Missing configuration. Set VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, and VITE_AUTH_CALLBACK_URL.'
      );
      return null;
    }

    const authConfig: AuthConfig = {
      auth0Domain,
      auth0ClientId,
      callbackUrl,
      siteId: config.siteId,
    };

    return new AuthManager(
      authConfig,
      (accessToken, siteId) => apiClient.exchangeForMASToken(accessToken, siteId),
      async (profile) => {
        // Register user in backend after login
        const registeredUser = await apiClient.registerUser({
          authProviderId: profile.sub,
          email: profile.email,
          name: profile.name,
          givenName: profile.given_name,
          familyName: profile.family_name,
          picture: profile.picture,
        });
        return { id: registeredUser.id };
      }
    );
  }, [config.siteId, apiClient]);

  // State variables - initialized from authManager if available (for page refresh case)
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => authManager?.isAuthenticated() ?? false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(() => authManager?.getCurrentUser() ?? null);
  const [internalUserId, setInternalUserId] = useState<string | null>(() => authManager?.getInternalUserId() ?? null);
  const [idToken, setIdToken] = useState<string | null>(() => authManager?.getIdToken() ?? null);

  // Handle auth callback and check initial auth state
  useEffect(() => {
    const initAuth = async () => {
      if (!authManager) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if we're returning from auth callback (URL has #access_token=)
        if (window.location.hash.includes('access_token=')) {
          await authManager.handleCallback();
          // Dispatch event for other components to react
          window.dispatchEvent(new CustomEvent('pantheon-auth-success'));
        }

        // Check current auth state and get user profile
        setIsAuthenticated(authManager.isAuthenticated());
        setCurrentUser(authManager.getCurrentUser());
        setInternalUserId(authManager.getInternalUserId());
        setIdToken(authManager.getIdToken());  // For API requests (hackweek - TODO: upgrade to MAS token)
      } catch (err) {
        console.error('[AuthManager] Initialization error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [authManager]);

  // Listen for auth state changes (login/logout from other tabs, etc.)
  useEffect(() => {
    const handleAuthSuccess = () => {
      if (authManager) {
        setIsAuthenticated(authManager.isAuthenticated());
        setCurrentUser(authManager.getCurrentUser());
        setInternalUserId(authManager.getInternalUserId());
        setIdToken(authManager.getIdToken());  // For API requests (hackweek - TODO: upgrade to MAS token)
      }
    };

    window.addEventListener('pantheon-auth-success', handleAuthSuccess);
    return () => {
      window.removeEventListener('pantheon-auth-success', handleAuthSuccess);
    };
  }, [authManager]);

  const value: AuthManagerContextValue = {
    authManager,
    isAuthenticated,
    isLoading,
    error,
    currentUser,
    internalUserId,
    idToken,  // For API requests (hackweek - TODO: upgrade to MAS token)
  };

  return (
    <AuthManagerContext.Provider value={value}>
      {children}
    </AuthManagerContext.Provider>
  );
};

/**
 * Hook to access AuthManager from context
 */
export function useAuthManager(): AuthManagerContextValue {
  const context = useContext(AuthManagerContext);
  if (!context) {
    throw new Error('useAuthManager must be used within an AuthManagerProvider');
  }
  return context;
}

/**
 * Optional hook that returns null when used outside AuthManagerProvider
 * Useful for components that work both with and without auth
 */
export function useAuthManagerOptional(): AuthManagerContextValue | null {
  return useContext(AuthManagerContext);
}
